import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/** Desktop stat card with icon chip + optional delta. Pass `onClick` to make it a button that links to its detail. */
export function DStat({
  value,
  label,
  sub,
  tone = 'var(--sage)',
  icon,
  delta,
  onClick,
}: {
  value: ReactNode;
  label: string;
  sub?: ReactNode;
  tone?: string;
  icon: IconName;
  delta?: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span className="eyebrow">{label}</span>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `color-mix(in srgb, ${tone} 16%, var(--card))`, display: 'grid', placeItems: 'center', color: tone }}>
          <Icon name={icon} size={17} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1 }}>{value}</div>
        {delta && <span style={{ fontSize: 12.5, color: tone, fontWeight: 600 }}>{delta}</span>}
      </div>
      {sub && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 8 }}>{sub}</div>}
    </>
  );
  if (onClick) {
    return (
      <button className="card" onClick={onClick} style={{ padding: 18, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', width: '100%', display: 'block' }}>
        {body}
      </button>
    );
  }
  return <div className="card" style={{ padding: 18 }}>{body}</div>;
}

export interface Column {
  label: string;
  align?: 'left' | 'center' | 'right';
}

/** Lightweight data table. */
export function DataTable<T>({
  cols,
  rows,
  renderRow,
}: {
  cols: Column[];
  rows: T[];
  renderRow: (row: T, i: number) => ReactNode;
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th
                key={i}
                style={{
                  textAlign: c.align ?? 'left',
                  padding: '11px 14px',
                  fontSize: 11,
                  fontFamily: 'var(--mono)',
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-3)',
                  fontWeight: 500,
                  borderBottom: '1px solid var(--line)',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--line)' : 'none' }}>
              {renderRow(r, i)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Td({ children, align, style }: { children: ReactNode; align?: 'left' | 'center' | 'right'; style?: CSSProperties }) {
  return <td style={{ padding: '13px 14px', textAlign: align ?? 'left', verticalAlign: 'middle', ...style }}>{children}</td>;
}

/** Toggle switch. */
export function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange?.(!on)}
      style={{
        width: 40,
        height: 23,
        borderRadius: 99,
        flex: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        background: on ? 'var(--sage)' : 'var(--line-2)',
        position: 'relative',
        transition: 'background .18s',
      }}
    >
      <span style={{ position: 'absolute', top: 2.5, left: on ? 19.5 : 2.5, width: 18, height: 18, borderRadius: '50%', background: 'var(--on-accent)', transition: 'left .18s' }} />
    </button>
  );
}
